import { FC, Fragment, useMemo, useState } from 'react';

import { DateTimeUtils } from '@servicetitan/datetime-utils';
import {
    BodyText,
    Button,
    Divider,
    Drawer,
    Eyebrow,
    Form,
    Stack,
} from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';

import { AppScopesVersionViewModel } from '../models/app-scopes-version-view-model';
import { getDisplayNamesString } from '../utils';
import { AppVersionStatusTag } from './app-version-status-tag';

export interface AppScopesVersionsDrawerProps {
    scopesVersions: AppScopesVersionViewModel[];
    showsApprovalStatus: boolean;
    onClose: () => void;
}

export const AppScopesVersionsDrawer: FC<AppScopesVersionsDrawerProps> = ({
    scopesVersions,
    showsApprovalStatus,
    onClose,
}) => {
    const [dateTimeUtils] = useDependencies(DateTimeUtils);

    const scopesVersionOptions = useMemo(
        () =>
            scopesVersions.map((v, index) => {
                const text = `Version ${v.version.toString()}${index === 0 ? ' (Current)' : ''}`;
                return {
                    text,
                    value: v,
                    content: (
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={2}
                        >
                            <Stack direction="column" spacing={1}>
                                <BodyText size="small">{text}</BodyText>
                                <BodyText
                                    size="xsmall"
                                    subdued
                                >{`Created on ${dateTimeUtils.formatDateFull(
                                    v.createdOn,
                                )} at ${dateTimeUtils.formatFullTime(v.createdOn)} by ${
                                    v.createdBy
                                }`}</BodyText>
                            </Stack>
                            {showsApprovalStatus && (
                                <AppVersionStatusTag version={v} subtle compact />
                            )}
                        </Stack>
                    ),
                };
            }),
        [scopesVersions, dateTimeUtils, showsApprovalStatus],
    );

    const [selectedScopesVersion, setSelectedScopesVersion] = useState(scopesVersions[0]);
    const { review, reasonLabel } = selectedScopesVersion;

    return (
        <Drawer
            backdrop
            width="m"
            header="Version History"
            footer={
                <Button primary onClick={onClose}>
                    Close
                </Button>
            }
            open
            onClose={onClose}
        >
            <Stack direction="column" spacing={2}>
                <Form.AnvilSelect
                    label="Version"
                    trigger={{ clearable: false }}
                    options={scopesVersionOptions}
                    value={scopesVersionOptions.find(o => o.value === selectedScopesVersion)}
                    onChange={({ value }: { value: AppScopesVersionViewModel }) => {
                        setSelectedScopesVersion(value);
                    }}
                    className="m-b-0"
                />
                {showsApprovalStatus && (
                    <Stack direction="column" spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <AppVersionStatusTag version={selectedScopesVersion} subtle />
                            {reasonLabel && <BodyText size="small">{reasonLabel}</BodyText>}
                        </Stack>
                        {review && (
                            <BodyText size="small" subdued>
                                {`Reviewed by ${review.reviewedBy} on ${dateTimeUtils.formatDateFull(
                                    review.reviewedOn,
                                )} at ${dateTimeUtils.formatFullTime(review.reviewedOn)}`}
                            </BodyText>
                        )}
                    </Stack>
                )}
                <Divider alignContent="start">
                    <Eyebrow size="large" className="c-neutral-400 m-r-1">
                        APIs
                    </Eyebrow>
                </Divider>
                <Eyebrow>Scopes</Eyebrow>
                <Stack.Item>
                    <ul className="m-y-0">
                        <li>
                            {`Read: ${getDisplayNamesString(
                                selectedScopesVersion.authScopes.filter(s => s.read),
                            )}`}
                        </li>
                        <li className="m-t-2">
                            {`Write: ${getDisplayNamesString(
                                selectedScopesVersion.authScopes.filter(s => s.write),
                            )}`}
                        </li>
                    </ul>
                </Stack.Item>
                {selectedScopesVersion.webhookEvents.length > 0 && (
                    <Fragment>
                        <Divider alignContent="start">
                            <Eyebrow size="large" className="c-neutral-400 m-r-1">
                                Webhooks
                            </Eyebrow>
                        </Divider>
                        <Eyebrow>Events</Eyebrow>
                        <Stack.Item>
                            <ul className="m-y-0">
                                <li>
                                    {`Receive: ${getDisplayNamesString(
                                        selectedScopesVersion.webhookEvents,
                                    )}`}
                                </li>
                            </ul>
                        </Stack.Item>
                    </Fragment>
                )}
            </Stack>
        </Drawer>
    );
};
AppScopesVersionsDrawer.displayName = 'AppScopesVersionsDrawer';
